import React, { useMemo, useState } from 'react'
import { Box, Button, Divider, List, Skeleton, Tab, Tabs, Typography } from '@mui/material'
import { useStyles } from './style'
import {
  NOTIFICATION_EMPTY_COPY,
  NOTIFICATION_ERROR_COPY,
  PANEL_MAX_ITEMS
} from './constants'
import { NotificationItem } from './notification-item'

export const TABS = [
  { key: 'unread', label: 'Unread' },
  { key: 'all', label: 'All' }
]

const SKELETON_ROWS = 3

const filterByTab = (notifications, tabKey) => {
  if (tabKey === 'unread') return notifications.filter(n => !n.read)
  return notifications
}

/**
 * Wireframe-spec panel: header + "Mark all read", Unread/All tabs, the list,
 * and a "View all notifications" footer.
 *
 * Presentational — it renders whatever it is handed and reports intent
 * upwards. No fetching, no persistence. `loading` and `error` are supplied by
 * the caller so this is ready for the real API without further change;
 * `error` takes precedence over `loading`, which takes precedence over the
 * empty state.
 */
export const NotificationPanel = ({
  notifications = [],
  loading = false,
  error = null,
  onRetry,
  onMarkAllRead,
  onItemClick,
  onTabChange,
  onViewAll,
  showHandle = false
}) => {
  const classes = useStyles()
  const [activeTab, setActiveTab] = useState('unread')

  const handleTabChange = (evt, value) => {
    setActiveTab(value)
    onTabChange?.(value)
  }

  // Capped, not scrolled forever: the full page owns the archive, and the slice
  // it reads from grows every time that page appends another one.
  const visible = useMemo(
    () => filterByTab(notifications, activeTab).slice(0, PANEL_MAX_ITEMS),
    [notifications, activeTab]
  )
  const hasUnread = useMemo(
    () => notifications.some(n => !n.read),
    [notifications]
  )

  const renderBody = () => {
    if (error) {
      return (
        <Box className={classes.stateBlock} role='alert' data-test='notification-error-state'>
          <Typography className={classes.stateTitle}>{NOTIFICATION_ERROR_COPY.title}</Typography>
          <Typography className={classes.stateBody}>{NOTIFICATION_ERROR_COPY.body}</Typography>
          {onRetry && (
            <Button
              variant='contained'
              className={classes.retryButton}
              onClick={onRetry}
              data-test='notification-retry'
            >
              {NOTIFICATION_ERROR_COPY.retry}
            </Button>
          )}
        </Box>
      )
    }

    if (loading) {
      return (
        <Box className={classes.list} data-test='notification-loading-state'>
          {/* Announced rather than silent — same approach as AccelerateSkeleton. */}
          <Box component='span' className={classes.srOnly}>
            <Typography aria-live='polite' aria-busy='true' data-test='notification-loading-message'>
              Loading notifications...
            </Typography>
          </Box>
          {Array.from({ length: SKELETON_ROWS }, (unused, index) => (
            <Box key={index} className={classes.skeletonRow} aria-hidden='true'>
              <Skeleton variant='circular' width={40} height={40} />
              <Box className={classes.skeletonLines}>
                <Skeleton variant='text' width='70%' />
                <Skeleton variant='text' width='45%' />
              </Box>
            </Box>
          ))}
        </Box>
      )
    }

    if (visible.length === 0) {
      const copy = NOTIFICATION_EMPTY_COPY[activeTab]
      return (
        <Box className={classes.stateBlock} data-test='notification-empty-state'>
          <Typography className={classes.stateTitle}>{copy.title}</Typography>
          <Typography className={classes.stateBody}>{copy.body}</Typography>
        </Box>
      )
    }

    return (
      <List className={classes.list} data-test='notification-list'>
        {visible.map((notification, index) => (
          <React.Fragment key={notification.id}>
            {index > 0 && <Divider component='li' />}
            <NotificationItem
              notification={notification}
              onClick={() => onItemClick?.(notification)}
              dataTest={`notification-item-${index}`}
            />
          </React.Fragment>
        ))}
      </List>
    )
  }

  return (
    // role='dialog' makes the bell's aria-haspopup='dialog' honest — MUI's
    // Popover paper carries no role of its own.
    <Box
      className={classes.panel}
      role='dialog'
      aria-label='Notifications'
      data-test='notification-panel'
    >
      {showHandle && (
        <Box className={classes.mobileHandle} aria-hidden='true'>
          <Box className={classes.mobileHandleBar} />
        </Box>
      )}

      <Box className={classes.header}>
        <Typography component='h2' className={classes.title}>
          Notifications
        </Typography>
        <Button
          className={classes.markAllReadButton}
          onClick={onMarkAllRead}
          disabled={!hasUnread || loading || Boolean(error)}
          disableRipple
          data-test='notification-mark-all-read'
        >
          Mark all read
        </Button>
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        className={classes.tabs}
        aria-label='Filter notifications'
      >
        {TABS.map(({ key, label }) => (
          <Tab
            key={key}
            value={key}
            label={label}
            className={classes.tab}
            disableRipple
            data-test={`notification-tab-${key}`}
          />
        ))}
      </Tabs>

      {renderBody()}

      <Box className={classes.footer}>
        <Button
          className={classes.footerButton}
          onClick={onViewAll}
          disableRipple
          data-test='notification-view-all'
        >
          View all notifications
        </Button>
      </Box>
    </Box>
  )
}

export default NotificationPanel
